import { Divider, Modal, Stack, TextField, Typography, Select, MenuItem, Paper, List, ListItem, ListItemText, ListItemIcon, Grid } from "@mui/material"
import React, { useContext, useEffect, useRef } from "react";
import { useState } from "react";
import { useMattoState } from "../../../MattoState";
import { CollectionInterface, CollectionProjectInterface, CollectionUserImages, CollectionUserMaterials, BoardSchemeInterface } from "ts-interfaces";
import { useFormik } from "formik";
import { encodeHTML, generateID } from "../../../libs/util";
import { AuthContext } from "../../../auth/Auth";
import { getDocByID } from "../../../libs/firebase";
import { produce } from "immer";
import { useLoginState } from "../../auth/LoginState";
import { ProfileUpload } from "../../../components/ProfileUpload";
import { ActionButton, ActionButtonSecondary } from "../../../buttons/ButtonStyles";
import { useCollections } from "../../../global-state/useCollections";
import { useBoards } from "../../../global-state/useBoards";
import { usePermissions, firebaseHelper } from "ui";
import useEventListener from "../../../libs/useEventListener";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';


const textFieldProps:any = { borderradius:'5px', variant: "outlined", margin: "normal", size: 'small', fullWidth: true, sx: { paddingRight: '10px',width:'98%' }}
const selectProps:any = { borderradius:'5px', variant: "outlined", size: 'small', fullWidth: true, sx: { paddingRight: '10px',width:'98%' }}
// const textFieldProps2:any = { borderradius:'5px', variant: "outlined", margin: "normal", size: 'small', fullWidth: true, sx: { padding: '0px',width:'98%' }}

const style = {
    position: 'absolute' as const,
    top: '50%',left: '50%',
    transform: 'translate(-50%, -50%)',width: 400,
    bgcolor: 'background.paper',borderRadius: 2,
    boxShadow: 24,p: 4,
};

interface CollectionModalProps {
    name: string;
    id: string;
    product?: string;
    userImage?: CollectionUserImages;
    userMaterial?: CollectionUserMaterials;
}
export const CollectionModal = () => {
    const {currentUser} = useContext(AuthContext);
    const modal:CollectionModalProps | any = useMattoState((state) => state.modal);
    const isCollectionModal = modal?.name=='collection';
    if (!isCollectionModal || currentUser==null) return null;
    else { return <NewCollectionModal /> }
}
export const NewCollectionModal = () => {
    const {currentUser} = useContext(AuthContext);
    const modal:CollectionModalProps | any = useMattoState((state) => state.modal);
    const isCollectionModal = modal?.name=='collection';
    const isNewCollection = modal?.id==null;
    const showShareCollection = modal?.showShareCollection ?? true;
    const associatedProjectsList = modal?.associatedProjectsList || [];
    const associatedProjectId = modal?.associatedProjectId || null;
    const onSave = modal?.onSave || null;
    const setModal = useMattoState((state) => state.setModal);
    const handleClose = () => void setModal(null);
    const [saving,setSaving] = useState(false);
    const [collection,setCollection] = useState<CollectionInterface | null>();
    const [coverImage,setCoverImage] = useState<any>(null);
    const setSnackbarMessage = useMattoState((state) => state.setSnackbarMessage);
    const [errors, setErrors]= useState<any>({})
    const addOrUpdateCollection = useLoginState ((state) => state.addOrUpdateCollection)
    const [projects, setProjects] = useState<any>([]);
    
    // Board selection state
    const [showBoardDropdown, setShowBoardDropdown] = useState(false);
    const [selectedBoard, setSelectedBoard] = useState<BoardSchemeInterface | null>(null);
    const [initialBoardId, setInitialBoardId] = useState<string | null>(null);
    const [boardSearchTerm, setBoardSearchTerm] = useState<string>('');
    const boardDropdownRef = useRef<HTMLDivElement | null>(null);
    
    // Get boards and permissions
    const { userBoards, sharedBoards, isLoading: boardsLoading } = useBoards((state) => ({
        userBoards: state.userBoards,
        sharedBoards: state.sharedBoards,
        isLoading: state.isLoading
    }));
    
    const { permissions } = usePermissions(currentUser?.uid, firebaseHelper.db);

    // Handle click outside dropdown
    function handleClickOutside(event: MouseEvent) {
        if (showBoardDropdown && boardDropdownRef.current && !boardDropdownRef.current.contains(event.target as Node)) {
            setShowBoardDropdown(false);
            setBoardSearchTerm(''); // Clear search when clicking outside
        }
    }

    useEventListener('mousedown', handleClickOutside);

    const formik:any = useFormik({
        initialValues: {
            name: collection?.name || '',
            description: collection?.description || '',
            associatedProjectId: associatedProjectId,
            associatedBoardUrl: collection?.associatedBoardUrl || '',
        },
        onSubmit: () => {  },
    });

    // Load boards when user changes
    useEffect(() => {
        if (currentUser?.uid) {
            useBoards.getState().getBoards(currentUser.uid, permissions);
        }
    }, [currentUser?.uid, permissions?.boardRead, permissions?.boardWrite]);

    // Force refresh boards when modal opens if no boards are loaded
    useEffect(() => {
        if (isCollectionModal && currentUser?.uid && !boardsLoading && userBoards.length === 0 && sharedBoards.length === 0) {
            // Ensure the state is properly initialized before calling getNext
            const boardsState = useBoards.getState();
            if (boardsState.currentUserId !== currentUser.uid) {
                useBoards.setState({ 
                    currentUserId: currentUser.uid, 
                    permissionsRef: permissions,
                    initialFetchDone: false,
                    lastDoc: null,
                    hasMore: true
                });
            }
            // Force a refresh by calling getNext directly
            useBoards.getState().getNext(true, 30);
        }
    }, [isCollectionModal, currentUser?.uid, boardsLoading, userBoards.length, sharedBoards.length, permissions]);

    useEffect(() => {
        setSaving(false); setErrors({}); setSnackbarMessage(null);
        setSelectedBoard(null); setInitialBoardId(null);
        if (modal?.name=='collection' && modal?.id) {
            getDocByID('usersMyCollection',modal.id).then(async (doc:any) => {
                if (doc.exists) {
                    const collection:CollectionInterface = doc.data() as CollectionInterface
                    formik.values.name = collection.name;
                    formik.values.description = collection.description;
                    if(collection?.associatedProjectId) formik.values.associatedProjectId = collection?.associatedProjectId;
                    setCollection(doc.data() as CollectionInterface)
                    if(collection?.projects) {
                        collection.projects.forEach((project: CollectionProjectInterface) => {
                            project.validURL = true;
                            project.owner = project.user == currentUser?.uid;
                        })
                        setProjects(collection.projects);
                    }
                    if(collection?.associatedBoardUrl && collection?.associatedBoardUrl?.length > 0) {
                        setInitialBoardId(collection.associatedBoardUrl);
                        formik.values.associatedBoardUrl = collection.associatedBoardUrl;
                        // Find the board from the URL and set it as selected
                        const boardId = collection.associatedBoardUrl.split('/').pop();
                        if (boardId) {
                            setInitialBoardId(boardId);
                        }
                    }
                }
            })
        }
        else if (currentUser) { setCollection(createNewCollection(currentUser?.uid)) }
        else { setCollection(null)}
    },[modal])

    // Find and set the board when boards are loaded
    useEffect(() => {
        if (initialBoardId && (userBoards.length > 0 || sharedBoards.length > 0)) {
            const allBoards = [...userBoards, ...sharedBoards];
            const foundBoard = allBoards.find(board => board.id === initialBoardId);
            if (foundBoard) {
                setSelectedBoard(foundBoard);
            }
        }
    }, [initialBoardId, userBoards, sharedBoards]);

 
    const handleSave = async () => {
        setErrors({})
        // if (!vaildate()) return;
        setSaving(true);

        const newCollection:CollectionInterface = produce(collection, (draft:CollectionInterface) => {
            draft.name = encodeHTML(formik.values.name) || '';
            draft.description = encodeHTML(formik.values.description) || '';
            draft.associatedProjectId = formik.values.associatedProjectId || null;
            draft.associatedBoardUrl = encodeHTML(formik.values.associatedBoardUrl) || '';
            draft.updatedAt = Date.now();
            if (modal.product) {
                if (!draft.products)  draft.products = []
                if (!draft.products.includes(modal.product.id)) {
                    draft.products.push(modal.product.id)
                    if (!draft.thumbnails) draft.thumbnails = []
                    draft.thumbnails.push({id:modal.product.id,src:modal.product.src})
                }
            }
            if (modal.userImage) {
                if (!draft.userImages) draft.userImages = []
                if (!draft.userImages.includes(modal.userImage)) {
                    draft.userImages.push(modal.userImage)
                    if (!draft.thumbnails) draft.thumbnails = []
                    draft.thumbnails.push({id:modal.userImage.id,src:modal.userImage.src})
                }
            }
            if(modal.userMaterial) {
                if (!draft.userMaterials) draft.userMaterials = []
                if (!draft.userMaterials.includes(modal.userMaterial)) {
                    draft.userMaterials.push(modal.userMaterial)
                    if (!draft.thumbnails) draft.thumbnails = []
                    draft.thumbnails.push({id:modal.userMaterial.id,src:modal.userMaterial.src})
                }
            }
            if(projects.length > 0) {
                draft.projects = projects.map((project) => {
                    return {id: project.id, name: project.name, thumbnail: project.thumbnail, url: project.url, user: project.user}
                })
            }
        }) as CollectionInterface
        setCollection(newCollection);
        onSave?.(newCollection);

        addOrUpdateCollection(newCollection, coverImage).then(() => {
            if (modal.product) useMattoState.getState().setSnackbarMessage(`${modal?.product?.name} updated in ${newCollection.name}`);
            else if (modal.userImage) useMattoState.getState().setSnackbarMessage(`Image updated in ${newCollection.name}`);
            else if (modal.userMaterial) useMattoState.getState().setSnackbarMessage(`${modal?.userMaterial?.name} updated in ${newCollection.name}`);
            else useMattoState.getState().setSnackbarMessage(`Collection ${newCollection.name} saved`);

            useCollections.getState().setCollections([newCollection, ...useCollections.getState().collections]);
        })
        .catch(err =>  {
            console.log("Error Saving Collection!" + err)
            useMattoState.getState().setSnackbarMessage(`Error Saving Collection! ${err}`)
        })
        .finally( () => { setSaving(false); handleClose() })
    }
    const handleCoverImageUpload = (blob) => {if (blob) setCoverImage(blob)    }

    const handleCoverImageDelete = () => { 
        setCoverImage(null);
        // Also update the collection's coverImage so ProfileUpload reflects the change
        setCollection(prev => prev ? { ...prev, coverImage: '' } : prev);
    }

    const handleShareCollection = () => {
        if (collection) {
            navigator.clipboard.writeText(`${window.location.origin}/project/new?collectionId=${collection.id}`)
            setSnackbarMessage(`Collection URL copied to clipboard`)
        }
    }

    // const addTextBox = (e) => {
    //     e.preventDefault();
    //     setProjects([...projects, {id:null,url:'',thumbnail:null,validURL:true,isLoading:false}]);
    //   };

    //   const removeTextBox = (index) => {
    //     const updatedTextBoxes = [...projects];
    //     updatedTextBoxes.splice(index, 1);
    //     setProjects(updatedTextBoxes);
    //   };

    //   const handleTextBoxChange = (e) => {
    //     const index = e.target.id.slice(-1)
    //     const value = e.target.value
    //     const updatedTextBoxes = [...projects];
    //     updatedTextBoxes[index]=  {id:null,url:value,thumbnail:null,validURL:true,isLoading:false};
    //     setProjects(updatedTextBoxes);
    //   };
    //   const handleTextBoxBlur = (e) => {
    //     const index = e.target.id.slice(-1)
    //     const value = e.target.value
    //     const id = value?.split('?')[0].split('/').pop()
    //     if(!id || id?.length <= 0 || (projects[index]?.id == id && projects[index]?.thumbnail)) return;
    //     const updatedTextBoxes = [...projects];
    //     updatedTextBoxes[index].isLoading = true;
    //     setProjects(updatedTextBoxes);
    //     loadProjectData(id,index,value)
    //   };

    //   const loadProjectData = async(id,index,value) => {
    //     const updatedTextBoxes = [...projects];
    //     getDocByID("projects", id).then((doc:any) => {
    //         if (!doc.exists) {
    //             updatedTextBoxes[index].validURL = false;
    //             return;
    //         }
    //         const data = doc.data();
    //         updatedTextBoxes[index] = { id:id, url:value, name: data?.projectData?.title, user: data?.user, thumbnail:data?.files?.screenshot, validURL:true, owner: data?.user == currentUser?.uid};
    //     })
    //     .catch((error) => {
    //         console.log("Error getting project: ", error);
    //         updatedTextBoxes[index].validURL = false;
    //       })
    //     .finally(() => {
    //         updatedTextBoxes[index].isLoading = false;
    //         setProjects(updatedTextBoxes);
    //     });
    //   }

    // const validateProject = (e) => {
    //     let validURL = true;
    //     const index = e.target.id.slice(-1)
    //     const value = e.target.value
    //     if (!value || !projects[index]?.thumbnail) {
    //       validURL = false;
    //     } else {
    //       validURL = true;
    //     }
    //     const updatedTextBoxes = [...projects];
    //     updatedTextBoxes[index].validURL = validURL;
    //     setProjects(updatedTextBoxes);
    // };

    // const handleKeyDown = (event) => {
    //     if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
    //       event.stopPropagation();
    //     }
    //   };

    if (!isCollectionModal || currentUser==null || collection==null) return null;

    // Handle board selection
    const handleToggleBoardDropdown = () => {
        if (showBoardDropdown) {
            // Clear search when closing dropdown
            setBoardSearchTerm('');
        }
        setShowBoardDropdown(!showBoardDropdown);
    };

    const handleSelectBoard = (board: BoardSchemeInterface | null) => {
        setSelectedBoard(board);
        setShowBoardDropdown(false);
        setBoardSearchTerm(''); // Clear search when selecting a board
        
        // Update formik value
        if (board) {
            const boardUrl = `${window.location.origin}/project/${board.id}`;
            formik.setFieldValue('associatedBoardUrl', boardUrl);
        } else {
            formik.setFieldValue('associatedBoardUrl', '');
        }
    };

    // Get boards with write access
    const getBoardsWithWriteAccess = () => {
        if (!permissions) return [];
        const writeBoardIds = new Set(permissions.boardWrite || []);
        return sharedBoards.filter(board => writeBoardIds.has(board.id));
    };

    // Filter boards based on search term
    const filterBoardsBySearch = (boards: BoardSchemeInterface[]) => {
        if (!boardSearchTerm.trim()) return boards;
        
        const searchLower = boardSearchTerm.toLowerCase();
        return boards.filter(board => {
            const boardName = board.projectData?.title || board.name || 'Untitled Board';
            return boardName.toLowerCase().includes(searchLower);
        });
    };

    // Render board dropdown options
    const renderNoneOption = () => {
        return (
            <div>
                <div style={{ padding: '8px 16px' }}>
                    <List sx={{ pt: 0, pb: 0 }}>
                        <ListItem 
                            data-testid="board-option-none"
                            onClick={() => handleSelectBoard(null)}
                            sx={{ 
                                cursor: 'pointer', 
                                backgroundColor: selectedBoard === null ? '#f5f5f5' : 'transparent',
                                '&:hover': { backgroundColor: '#f0f0f0' },
                                py: 0,
                                px: 3,
                                minHeight: '32px',
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: '24px' }}>
                                {selectedBoard === null && (
                                    <CheckIcon 
                                        sx={{ 
                                            fontSize: '18px', 
                                            color: '#c58ed4' 
                                        }} 
                                    />
                                )}
                            </ListItemIcon>
                            <ListItemText 
                                primary="None"
                                sx={{ 
                                    '& .MuiListItemText-primary': { 
                                        fontSize: '14px',
                                        fontStyle: 'italic',
                                        color: '#666'
                                    }
                                }}
                            />
                        </ListItem>
                    </List>
                </div>
                <div style={{ 
                    height: '1px', 
                    backgroundColor: '#e0e0e0', 
                    margin: '0px 16px' 
                }} />
            </div>
        );
    };

    const renderBoardList = (boards: BoardSchemeInterface[], sectionTitle: string, isLastSection = false) => {
        if (boards.length === 0) return null;
        
        return (
            <div>
                <Typography variant="h6" sx={{ p: 1, pl: 2, pb: 0, fontWeight: 'bold' }}>
                    {sectionTitle}
                </Typography>
                <div style={{ maxHeight: '200px', overflowY: 'auto', padding: '0 8px' }}>
                    <List sx={{ pt: 0, pb: 0 }}>
                        {boards.map((board, index) => {
                            const isSelected = selectedBoard?.id === board.id;
                            const boardName = board.projectData?.title || board.name || 'Untitled Board';
                            
                            return (
                                <ListItem 
                                    key={`${sectionTitle}-${index}`}
                                    data-testid={`board-option-${boardName.toLowerCase().replace(/\s+/g, '-')}`}
                                    onClick={() => handleSelectBoard(board)}
                                    sx={{ 
                                        cursor: 'pointer', 
                                        backgroundColor: isSelected ? '#f5f5f5' : 'transparent',
                                        '&:hover': { backgroundColor: '#f0f0f0' },
                                        py: 0,
                                        px: 3,
                                        minHeight: '32px',
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: '24px' }}>
                                        {isSelected && (
                                            <CheckIcon 
                                                sx={{ 
                                                    fontSize: '18px', 
                                                    color: '#c58ed4' 
                                                }} 
                                            />
                                        )}
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={boardName}
                                        sx={{ 
                                            '& .MuiListItemText-primary': { 
                                                fontSize: '14px'
                                            }
                                        }}
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                </div>
                {!isLastSection && (
                    <div style={{ 
                        height: '1px', 
                        backgroundColor: '#e0e0e0', 
                        margin: '8px 16px' 
                    }} />
                )}
            </div>
        );
    };

    return (
        <Modal open={modal?.name=='collection'} onClose={handleClose} aria-labelledby="modal-collection">
        <Stack sx={style}  >
            <Stack direction='row' spacing={2} justifyContent="space-between" alignItems="center" >
                <Typography id="modal-modal-title" variant="h6" component="h2" style={{fontWeight:'800'}}>
                    {isNewCollection ? 'Create New Collection' : 'Edit Collection' }
                </Typography>
                {/* {modal?.id && showShareCollection &&
                    <Stack alignItems='baseline'>
                        <ActionButtonSecondary variant="contained" onClick={handleShareCollection}>Share This Collection</ActionButtonSecondary>
                    </Stack>
                } */}
            </Stack>
            <Stack direction='row' spacing={2} justifyContent="space-between" sx={{ marginTop: '12px' }}>

            { (modal.userImage || modal.userMaterial || modal.product?.src)  &&   <img src={modal.userImage?.src || modal.userMaterial?.src || modal.product?.src}  style={{marginTop:'20px',maxHeight:'64px', borderRadius:'10px'}} />}
            <Stack style={{width:'100%'}}  >
                <TextField {...textFieldProps} id="name"  label="Collection Name" name="name" value={formik.values.name} onChange={formik.handleChange} />
                {errors.name && <Typography variant="caption" color="red">Collection name must be more than 3 characters</Typography> }
                <TextField {...textFieldProps} id="description"  label="Collection Description (Optional)" name="description" multiline rows={2} value={formik.values.description} onChange={formik.handleChange} />
                {errors.description && <Typography variant="caption" color="red">Description must be less than 300 characters</Typography> }

                {/* <Stack style={{ paddingBottom:'10px'}} >
                    <Typography color="#333" fontSize="16px" fontWeight="600">
                        Associated Project
                    </Typography>
                    <Select
                        {...selectProps}
                        id="associatedProjectId"
                        name="Associated Project"
                        value={formik.values.associatedProjectId}
                        onChange={(event) => formik.setFieldValue("associatedProjectId", event.target.value)}
                        displayEmpty
                        fullWidth
                    >
                        <MenuItem value="" disabled>
                            Select a Project
                        </MenuItem>
                        {associatedProjectsList.map((project: CollectionProjectInterface) => (
                            <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                        ))}
                    </Select>
                </Stack> */}

                <Stack style={{ paddingBottom:'10px'}} >
                    <Typography color="#333" fontSize="16px" fontWeight="600">
                        Associated Board
                    </Typography>
                    <Typography color="#828282" fontSize="12px" fontWeight="500">
                       This board will serve as a default board for this collection
                    </Typography>
                    
                    <Grid 
                        sx={{ 
                            position: 'relative',
                            width: '100%',
                            marginTop: '8px'
                        }}
                        ref={boardDropdownRef}
                    >
                        <Grid
                            data-testid="board-dropdown-trigger"
                            onClick={handleToggleBoardDropdown}
                            sx={{
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                p: 1.5,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                cursor: 'pointer',
                                color: selectedBoard ? 'black' : '#999',
                                minHeight: '40px',
                                backgroundColor: boardsLoading ? '#f5f5f5' : 'white'
                            }}
                        >
                            <Typography noWrap sx={{ maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {boardsLoading ? 'Loading boards...' : 
                                 selectedBoard 
                                    ? selectedBoard.projectData?.title || selectedBoard.name || 'Untitled Board'
                                    : 'Select Board (Optional)'}
                            </Typography>
                            <KeyboardArrowDownIcon />
                        </Grid>

                        {showBoardDropdown && !boardsLoading && (
                            <Paper
                                data-testid="board-dropdown-options"
                                elevation={3}
                                sx={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    width: '100%',
                                    zIndex: 1300,
                                    mt: 0.5,
                                    pb: 1,
                                    maxHeight: '300px',
                                    overflowY: 'auto'
                                }}
                            >
                                {/* Search input */}
                                <div style={{ padding: '8px 16px', borderBottom: '1px solid #e0e0e0' }}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="Search boards..."
                                        value={boardSearchTerm}
                                        onChange={(e) => setBoardSearchTerm(e.target.value)}
                                        onClick={(e) => e.stopPropagation()} // Prevent dropdown from closing when clicking input
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: '#d0d0d0',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#b0b0b0',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#c58ed4',
                                                },
                                            },
                                        }}
                                    />
                                </div>
                                
                                {renderNoneOption()}
                                {(() => {
                                    const sharedBoardsWithWriteAccess = getBoardsWithWriteAccess();
                                    const filteredUserBoards = filterBoardsBySearch(userBoards);
                                    const filteredSharedBoards = filterBoardsBySearch(sharedBoardsWithWriteAccess);
                                    const hasFilteredSharedBoards = filteredSharedBoards.length > 0;
                                    const hasFilteredUserBoards = filteredUserBoards.length > 0;
                                    
                                    // Show "No boards found" message if search term exists but no results
                                    if (boardSearchTerm.trim() && !hasFilteredUserBoards && !hasFilteredSharedBoards) {
                                        return (
                                            <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                                                <Typography variant="body2">
                                                    No boards found matching "{boardSearchTerm}"
                                                </Typography>
                                            </div>
                                        );
                                    }
                                    
                                    return (
                                        <>
                                            {hasFilteredUserBoards && renderBoardList(filteredUserBoards, "My Boards", !hasFilteredSharedBoards)}
                                            {hasFilteredSharedBoards && renderBoardList(filteredSharedBoards, "Shared with Me", true)}
                                        </>
                                    );
                                })()}
                            </Paper>
                        )}
                    </Grid>

                    {/* <table className="projects-table" style={{ width: '100%'}}>
                    <thead style={{visibility: 'hidden'}}>
                      <tr>
                        <th style={{width: "80%"}}></th>
                        <th style={{width: "20%"}}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {projects?.length > 0 && projects.map((project, index) => (
                        <tr key={'project'+ index} style={{padding: '0px !important'}}>
                          <td>
                              <TextField
                                {...textFieldProps2}
                                fullWidth
                                variant="outlined"
                                type= {"url"}
                                id={'id'+ index}
                                label="Project URL (Optional)"
                                data-testid="ProjectUrlForCollection"
                                value={project.url}
                                onKeyDown={handleKeyDown}
                                onChange={handleTextBoxChange}
                                onMouseOut={handleTextBoxBlur}
                                onBlur={validateProject}
                                error={!project?.validURL || project?.owner == false}
                                helperText={!project?.validURL ? 'Invalid Project Url!' : ((project?.owner == false) ? 'You can only add your own projects' : ' ' )}
                              />
                          </td>
                          <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                            {project.isLoading && !project?.thumbnail && project?.validURL && <CircularProgress />}
                            {project?.thumbnail && <img style={{marginTop: "-15px"}} data-testid="ProjectThumbnailForCollection" src={cloudflareCDN(getUrlFromRef(project.thumbnail),'height=40,format=auto')} loading="lazy" alt="" />}
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td>
                          {projects?.length < 1 &&
                            <ActionButton variant="contained" data-testid="AddProjectToCollection" onClick={addTextBox}>Add Project</ActionButton>
                          }
                        </td>
                      </tr>
                    </tbody>
                  </table> */}
                </Stack>

                <ProfileUpload disabled={saving} maxResizeWidth={500} onFileChange={handleCoverImageUpload} img={collection.coverImage} title='Cover Image' defaultText=' ' deleteOption={true} onDelete={handleCoverImageDelete}/>
                <Divider style={{paddingTop:'20px'}} />

                <Stack style={{paddingTop:'20px'}} spacing={2} direction={'row'} justifyContent="space-between">
                    <ActionButton variant="contained" onClick={()=> handleClose()} disabled={saving} >Cancel</ActionButton>
                    <ActionButton 
                        variant="contained" 
                        onClick={handleSave} 
                        disabled={saving || boardsLoading}
                    >
                        {saving ? 'Saving...' : 'Save Collection'}
                    </ActionButton>
                </Stack>
            </Stack>
            </Stack>
        </Stack>
        </Modal>
    )
}

export const createNewCollection = (currentUserUid) => {
    if (!currentUserUid) throw new Error("currentUserUid is required")
    const data:CollectionInterface = {
        id: generateID(),
        name: '',
        uid: currentUserUid,
        updatedAt: Date.now(),
        createdAt: Date.now(),
        protectedCollection: false,
        products: [],
        description: "",
        coverImage: "",
        userImages: [],
        userMaterials: [],
        thumbnails:[]
    };
    return data;
}
